// 경기 카드에서 띄우는 시트들 — 점수 입력, 미진행 처리.
//
// 시트 자체는 ui/sheet의 openSheet/closeSheet에 위임하고, 폼 동작과
// API 호출만 여기 모음. 저장/스킵 후 rerender는 호출 측이 onSaved
// 콜백으로 주입한다 (legacy.ts의 render()에 직접 의존하면 순환 import).

import { useRef, useState } from "preact/hooks";
import { api } from "../api";
import { state } from "../state";
import { mountPreact } from "../ui/preact-mount";
import { closeSheet, openSheet, toast, withBusy } from "../ui/sheet";
import { matchPlayerName, formatTeamName } from "./index";

interface SwapMatch {
  match_id: string;
  round_number: number;
  sides?: Array<{ player_ids?: string[] }>;
}

interface SwapEvent {
  event_id: string;
  mode?: string;
}

interface SwapCandidate {
  pid: string;
  match?: any;
  locked?: boolean;
  mismatch: boolean;
}

export interface ScoreSheetMatch {
  match_id: string;
  side_a_score?: number | string | null;
  side_b_score?: number | string | null;
  sides?: Array<{
    side_number: number;
    players?: any[];
  }>;
}

export interface ScoreSheetEvent {
  status: string;
}

function ScoreSheet({
  match,
  onSaved,
}: {
  match: ScoreSheetMatch;
  onSaved: () => void;
}) {
  const sides = match.sides || [];
  const sideA = sides.find((s) => s.side_number === 1) || { players: [] };
  const sideB = sides.find((s) => s.side_number === 2) || { players: [] };
  const namesA = (sideA.players || []).map((p: any) => matchPlayerName(p)).join(" · ")
    || formatTeamName(state.club?.default_team_a_name || "A");
  const namesB = (sideB.players || []).map((p: any) => matchPlayerName(p)).join(" · ")
    || formatTeamName(state.club?.default_team_b_name || "B");

  const [scoreA, setScoreA] = useState<string>(match.side_a_score != null ? String(match.side_a_score) : "");
  const [scoreB, setScoreB] = useState<string>(match.side_b_score != null ? String(match.side_b_score) : "");
  const saveRef = useRef<HTMLButtonElement>(null);

  async function onSubmit(e: Event) {
    e.preventDefault();
    const a = Number(scoreA);
    const b = Number(scoreB);
    if (!Number.isFinite(a) || !Number.isFinite(b) || a < 0 || b < 0) {
      toast("점수를 올바르게 입력해주세요");
      return;
    }
    try {
      await withBusy(saveRef.current, "저장 중…", () =>
        api(`/api/v1/matches/${match.match_id}/results`, {
          method: "POST",
          body: { side_a_score: a, side_b_score: b },
        }),
      );
      closeSheet();
      toast("점수가 저장되었어요");
      onSaved();
    } catch (err: any) {
      toast(err.message);
    }
  }

  async function onSkip() {
    try {
      await api(`/api/v1/matches/${match.match_id}/skip`, { method: "POST" });
      closeSheet();
      toast("미진행 처리했어요");
      onSaved();
    } catch (err: any) {
      toast(err.message);
    }
  }

  const inputStyle = "text-align:center;font-size:28px;font-weight:800";
  return (
    <div>
      <form class="form" onSubmit={onSubmit}>
        <div class="field">
          <span class="field__label">{namesA}</span>
          <input
            class="input"
            type="number"
            inputmode="numeric"
            min="0"
            required
            style={inputStyle}
            value={scoreA}
            onInput={(e) => setScoreA((e.currentTarget as HTMLInputElement).value)}
          />
        </div>
        <div style="text-align:center;color:var(--ink-soft);font-weight:700;font-size:12px">VS</div>
        <div class="field">
          <span class="field__label">{namesB}</span>
          <input
            class="input"
            type="number"
            inputmode="numeric"
            min="0"
            required
            style={inputStyle}
            value={scoreB}
            onInput={(e) => setScoreB((e.currentTarget as HTMLInputElement).value)}
          />
        </div>
        <button ref={saveRef} class="btn btn--accent btn--block" type="submit">저장</button>
      </form>
      <button class="btn btn--ghost btn--block" type="button" style="margin-top:4px" onClick={onSkip}>
        미진행 처리
      </button>
    </div>
  );
}

export function openScoreSheet(
  match: ScoreSheetMatch,
  ev: ScoreSheetEvent,
  onSaved: () => void,
): void {
  if (ev.status !== "in_progress" && ev.status !== "completed") {
    toast("일정을 먼저 시작해주세요 (시작 버튼)");
    return;
  }
  openSheet("점수 입력", mountPreact(<ScoreSheet match={match} onSaved={onSaved} />));
}

// =====================================================================
// openSwapSheet
// =====================================================================

function SwapRow({
  label,
  sub,
  locked,
  onClick,
}: {
  label: string;
  sub: string | null;
  locked?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      class={"list__item" + (locked ? " list__item--disabled" : "")}
      disabled={locked}
      onClick={onClick}
      style="width:100%;text-align:left"
    >
      <div class="list__main">
        <div class="list__title">{label}</div>
        {sub && <div class="list__meta">{sub}</div>}
      </div>
    </button>
  );
}

interface SwapBodyProps {
  sourceName: string;
  roundNumber: number;
  pMap: Record<string, any>;
  resting: SwapCandidate[];
  sameCourt: SwapCandidate[];
  otherCourt: SwapCandidate[];
  onSwap: (pid: string) => Promise<void>;
}

function SwapBody({ sourceName, roundNumber, pMap, resting, sameCourt, otherCourt, onSwap }: SwapBodyProps) {
  function genderTag(pid: string): string {
    const g = pMap[pid]?.gender_snapshot;
    if (g === "male") return "남";
    if (g === "female") return "여";
    return "";
  }
  const labelOf = (c: SwapCandidate) =>
    `${matchPlayerName(pMap[c.pid])}${genderTag(c.pid) ? " · " + genderTag(c.pid) : ""}`;

  const noneAvailable = resting.length === 0 && sameCourt.length === 0 && otherCourt.length === 0;

  return (
    <div class="stack">
      <div class="muted" style="padding:4px 0 8px">
        {sourceName} 선수와 교체할 대상을 선택하세요. (라운드 {roundNumber})
      </div>
      {resting.length > 0 && <div class="swap-section">휴식 중</div>}
      {resting.map((c) => (
        <SwapRow
          key={c.pid}
          label={labelOf(c)}
          sub={c.mismatch ? "잡복 비허용 (성별 다름)" : "휴식 → 코트 진입"}
          locked={c.mismatch}
          onClick={() => onSwap(c.pid)}
        />
      ))}
      {sameCourt.length > 0 && <div class="swap-section">같은 코트</div>}
      {sameCourt.map((c) => (
        <SwapRow
          key={c.pid}
          label={labelOf(c)}
          sub={c.mismatch ? "잡복 비허용 (성별 다름)" : `코트 내 자리 교체${c.locked ? " (결과 입력됨)" : ""}`}
          locked={c.locked || c.mismatch}
          onClick={() => onSwap(c.pid)}
        />
      ))}
      {otherCourt.length > 0 && <div class="swap-section">다른 코트</div>}
      {otherCourt.map((c) => (
        <SwapRow
          key={c.pid}
          label={labelOf(c)}
          sub={c.mismatch
            ? "잡복 비허용 (성별 다름)"
            : `코트 ${c.match?.court_label || "?"} 와 교체${c.locked ? " (결과 입력됨)" : ""}`}
          locked={c.locked || c.mismatch}
          onClick={() => onSwap(c.pid)}
        />
      ))}
      {noneAvailable && <div class="muted">교체 가능한 대상이 없습니다.</div>}
    </div>
  );
}

export async function openSwapSheet(
  match: SwapMatch,
  fromPid: string,
  ev: SwapEvent,
  onSwapped: () => Promise<void> | void,
): Promise<void> {
  let detail: any;
  try {
    detail = await api(`/api/v1/events/${ev.event_id}`);
  } catch (e: any) {
    toast(e.message);
    return;
  }
  const participants: any[] = detail.participants || [];
  const matches: any[] = detail.matches || [];
  const teams: any[] = detail.teams || [];
  const pMap: Record<string, any> = Object.fromEntries(
    participants.map((p) => [p.event_participant_id, p]),
  );
  const sourcePlayer = pMap[fromPid];
  const sourceName = sourcePlayer ? matchPlayerName(sourcePlayer) : "선수";
  const sourceGender = sourcePlayer?.gender_snapshot;
  const roundNumber = match.round_number;
  const isTeamMode = ev.mode === "team";
  const blockExceptionalMix = state.club?.allow_exceptional_mix === false;
  const genderMismatch = (pid: string) => {
    if (!blockExceptionalMix) return false;
    const g = pMap[pid]?.gender_snapshot;
    if (!sourceGender || !g) return false;
    return g !== sourceGender;
  };

  const teamOfPid: Record<string, string> = {};
  for (const t of teams) {
    for (const pid of t.participant_ids || []) teamOfPid[pid] = t.team_id;
  }
  const sourceTeamId = teamOfPid[fromPid];
  const sameTeam = (pid: string) => !isTeamMode || teamOfPid[pid] === sourceTeamId;

  const sameRoundMatches = matches.filter((m) => m.round_number === roundNumber);
  const playingIds = new Set<string>();
  for (const m of sameRoundMatches) (m.player_ids || []).forEach((id: string) => playingIds.add(id));

  const allowSameCourtSwap = !isTeamMode;
  const fromSide = (match.sides || []).find((s) => (s.player_ids || []).includes(fromPid));
  const sameSideIds = new Set<string>(fromSide ? fromSide.player_ids || [] : []);
  const otherCourt: SwapCandidate[] = [];
  const sameCourt: SwapCandidate[] = [];
  for (const m of sameRoundMatches) {
    const isSameMatch = m.match_id === match.match_id;
    if (isSameMatch && !allowSameCourtSwap) continue;
    const locked = m.result_status && m.result_status !== "empty";
    for (const pid of m.player_ids || []) {
      if (pid === fromPid) continue;
      if (!sameTeam(pid)) continue;
      if (isSameMatch) {
        if (sameSideIds.has(pid)) continue;
        sameCourt.push({ pid, match: m, locked, mismatch: genderMismatch(pid) });
      } else {
        otherCourt.push({ pid, match: m, locked, mismatch: genderMismatch(pid) });
      }
    }
  }
  const resting: SwapCandidate[] = participants
    .filter((p) => !playingIds.has(p.event_participant_id) && sameTeam(p.event_participant_id))
    .map((p) => ({
      pid: p.event_participant_id,
      mismatch: genderMismatch(p.event_participant_id),
    }));

  async function doSwap(toPid: string) {
    try {
      await api(`/api/v1/events/${ev.event_id}/matches/${match.match_id}/swap`, {
        method: "POST",
        body: { from_event_participant_id: fromPid, to_event_participant_id: toPid },
      });
      closeSheet();
      toast("선수를 교체했습니다");
      await onSwapped();
    } catch (e: any) {
      toast(e.message);
    }
  }

  const body = mountPreact(
    <SwapBody
      sourceName={sourceName}
      roundNumber={roundNumber}
      pMap={pMap}
      resting={resting}
      sameCourt={sameCourt}
      otherCourt={otherCourt}
      onSwap={doSwap}
    />,
  );
  openSheet(`${sourceName} 선수 교체`, body);
}
